import { listTimeZones } from '../../services/configuration/configuration.service';
import {
    addRecipientToGroup,
    editRecipientToGroup,
    getRecipient,
    removeRecipientFromGroup,
} from '../../services/distribution/distribution.service';
import type {
    CreateDistributionGroupRequest,
    CreateRecipientRequest,
    DistributionGroupDetail,
    DistributionRecipientDetail,
    DistributionRecipientSummary,
} from '../../services/distribution/distribution.types';
import type { TimeZoneSummary } from '../../services/configuration/configuration.types';

export interface DistributionGroupFormValues extends CreateDistributionGroupRequest {
    recipients?: CreateRecipientRequest[];
}

interface RecipientFormEntry extends CreateRecipientRequest {
    email?: string;
    sms?: string;
    emailRecipientId?: number;
    smsRecipientId?: number;
    bureauContactType?: 'email' | 'sms' | 'bureau';
}

export function getDistributionErrorMessage(error: unknown, fallback: string): string {
    if (typeof error === 'string' && error.trim()) return error;
    if (error && typeof error === 'object') {
        if ('message' in error && typeof error.message === 'string' && error.message.trim()) {
            return error.message;
        }
        const details = 'fieldErrors' in error ? error.fieldErrors : 'errors' in error ? error.errors : undefined;
        if (typeof details === 'string' && details.trim()) return details;
        if (Array.isArray(details)) {
            const messages = details.filter((message): message is string => typeof message === 'string' && Boolean(message.trim()));
            if (messages.length) return messages.join('\n');
        }
        if (details && typeof details === 'object') {
            const messages = Object.values(details).flat().filter((message): message is string => typeof message === 'string' && Boolean(message.trim()));
            if (messages.length) return messages.join('\n');
        }
    }
    return fallback;
}

export function buildRecipientPayload(recipient: CreateRecipientRequest): CreateRecipientRequest {
    const contactType = recipient.contactType?.trim().toUpperCase() || (recipient.isBureauRecipient ? 'BUREAU' : undefined);
    if (!contactType || !['EMAIL', 'SMS', 'BUREAU'].includes(contactType)) {
        throw new Error(`Invalid contactType: ${contactType}. Must be EMAIL, SMS or BUREAU`);
    }
    if (contactType === 'BUREAU') {
        return {
            firstName: 'Bureau',
            lastName: 'Bureau',
            contactType: 'BUREAU',
            contactValue: 'Bureau',
            contactLabel: '',
            scheduleId: recipient.scheduleId ?? 1,
            timeZoneId: recipient.timeZoneId ?? 1,
            recipientType: recipient.recipientType ?? 'SYSTEM',
            bureauFormat: recipient.bureauFormat ?? '',
            bureauEndpoint: recipient.bureauEndpoint ?? '',
            notes: recipient.notes ?? 'bur recipeint',
            recipientId: recipient.recipientId ?? undefined,
        };
    }
    return {
        firstName: recipient.firstName,
        lastName: recipient.lastName,
        contactType,
        contactValue: recipient.contactValue ?? '',
        contactLabel: recipient.contactLabel,
        scheduleId: recipient.scheduleId,
        timeZoneId: recipient.timeZoneId,
        recipientType: recipient.recipientType || (recipient.isBureauRecipient ? 'BUREAU' : 'SYSTEM'),
        bureauFormat: recipient.bureauFormat,
        bureauEndpoint: recipient.bureauEndpoint,
        notes: recipient.notes,
        recipientId: recipient.recipientId ?? undefined,
    };
}

export async function saveGroupRecipients(
    groupId: number,
    recipients: CreateRecipientRequest[],
    editing: boolean,
): Promise<string[]> {
    const failures: string[] = [];
    const existing = editing ? recipients.filter(recipient => recipient.recipientId != null) : [];
    const added = editing ? recipients.filter(recipient => recipient.recipientId == null) : recipients;
    for (const recipient of [...existing, ...added]) {
        try {
            const payload = buildRecipientPayload(recipient);
            const response = editing && payload.recipientId != null
                ? await editRecipientToGroup(payload.recipientId, payload)
                : await addRecipientToGroup(groupId, payload);
            if (!response.success) {
                throw new Error(getDistributionErrorMessage(response.error, 'Failed to save recipient'));
            }
        } catch (error: unknown) {
            failures.push(getDistributionErrorMessage(error, 'Failed to save recipient'));
        }
    }
    return failures;
}

export async function deleteGroupRecipient(groupId: number, recipientId: number): Promise<void> {
    const response = await removeRecipientFromGroup(groupId, recipientId);
    if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete recipient');
    }
}

function mergeRecipientDetail(
    summary: DistributionRecipientSummary,
    detail: DistributionRecipientDetail,
    timeZones: TimeZoneSummary[],
): DistributionRecipientDetail {
    const merged = { ...summary, ...detail };
    if (merged.timeZoneId == null && merged.timeZone) {
        const name = merged.timeZone.trim();
        const match = timeZones.find(zone =>
            zone.standardName === name || zone.displayName === name ||
            zone.displayName?.includes(name) || zone.standardName?.includes(name));
        if (match) merged.timeZoneId = match.id;
    }
    return merged;
}

export async function hydrateGroupRecipients(group: DistributionGroupDetail): Promise<DistributionGroupDetail> {
    const recipients = group.recipients ?? [];
    if (!recipients.some(recipient => recipient.timeZoneId == null)) return group;
    const timeZonesResponse = await listTimeZones();
    if (!timeZonesResponse.success || !timeZonesResponse.data) {
        throw new Error(timeZonesResponse.error?.message || 'Failed to load time zones');
    }
    const hydrated: DistributionRecipientSummary[] = [];
    for (const recipient of recipients) {
        if (recipient.timeZoneId != null) {
            hydrated.push(recipient);
            continue;
        }
        const response = await getRecipient(recipient.recipientId);
        if (!response.success || !response.data) {
            throw new Error(response.error?.message || 'Failed to load recipient details');
        }
        hydrated.push(mergeRecipientDetail(recipient, response.data, timeZonesResponse.data));
    }
    return { ...group, recipients: hydrated };
}

function mapRecipientToFormEntry(recipient: DistributionRecipientSummary): RecipientFormEntry {
    const parts = (recipient.displayName ?? '').split(' ');
    const isBureauRecipient = recipient.isBureauRecipient === true || recipient.recipientType?.toUpperCase() === 'BUREAU';
    const type = recipient.contactType?.toLowerCase();
    const contactMode = type === 'email' || type === 'sms' || type === 'bureau' ? type
        : recipient.isEmailRecipient ? 'email' : recipient.isSmsRecipient ? 'sms'
            : isBureauRecipient ? 'bureau' : undefined;
    const bureauContactType = isBureauRecipient ? (type === 'email' || type === 'sms' ? type : 'bureau') : undefined;
    const email = contactMode === 'email' ? recipient.contactValue ?? '' : '';
    const sms = contactMode === 'sms' ? recipient.contactValue ?? '' : '';
    return {
        firstName: parts[0] ?? '',
        lastName: parts.slice(1).join(' '),
        email,
        sms,
        recipientId: recipient.recipientId,
        emailRecipientId: contactMode === 'email' ? recipient.recipientId : undefined,
        smsRecipientId: contactMode === 'sms' ? recipient.recipientId : undefined,
        contactLabel: recipient.contactLabel,
        scheduleId: recipient.scheduleId != null ? Number(recipient.scheduleId) : undefined,
        timeZoneId: recipient.timeZoneId != null ? Number(recipient.timeZoneId) : undefined,
        isBureauRecipient,
        bureauContactType,
        contactType: bureauContactType || (email ? 'email' : sms ? 'sms' : 'email'),
        contactValue: email || sms || '',
    };
}

export function mapGroupToFormValues(group: DistributionGroupDetail): DistributionGroupFormValues {
    return {
        profileName: group.profileName ?? '',
        recipients: (group.recipients ?? []).map(mapRecipientToFormEntry),
    };
}
